import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import type { RefObject } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';

import {
  CAMPAIGN_SUPPORTED_PLATFORMS,
  CampaignLabelOptions,
  EMPTY_LABEL_VALUE,
  type CAMPAIGN_LABEL_TYPE,
  type CampaignDraftCreatorItem,
} from '@/entities/campaign';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { PageNavigationView } from '@/shared/components/page-navigation/page-navigation';
import { Alert, AlertDescription } from '@/shared/components/shadcn/alert';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Table, TableBody, TableCell, TableRow } from '@/shared/components/shadcn/table';
import { cn } from '@/shared/utils';

import { getCreatorCountBadge } from '@/features/campaign/helpers/campaign-create-ux';
import type { CampaignCreateIssue } from '@/features/campaign/hooks/campaign-create-shared';
import {
  CAMPAIGN_CREATE_TABLE_PAGE_SIZE,
  focusElement,
} from '../../../hooks/campaign-create-shared';
import { PlatformSelectLabel } from '../../primitives';

const TABLE_GRID = {
  creator: 'minmax(240px,1.8fr) minmax(160px,0.9fr) minmax(180px,1fr) 48px',
};

type CreatorRowProps = {
  editor: CreatorRowEditor;
  item: CampaignDraftCreatorItem;
  isInvalid: boolean;
  rowIssue?: string;
};

type CreatorRowActions = {
  blurSource: (rowId: string) => void;
  changeLabel: (rowId: string, label?: CAMPAIGN_LABEL_TYPE) => void;
  changePlatform: (rowId: string, platform: SOURCE_PLATFORM_TYPE) => void;
  changeSource: (rowId: string, sourceValue: string) => void;
  pasteSource: (rowId: string, sourceValue: string) => void;
  remove: (rowId: string) => void;
};

type CreatorRowEditor = {
  actions: CreatorRowActions;
  sourceRefs: RefObject<Record<string, HTMLInputElement | null>>;
};

type CreatorInputRows = {
  actions: CreatorRowActions;
  add: () => string | undefined;
  clear: () => void;
  items: CampaignDraftCreatorItem[];
  remainingSlots: number;
  sourceRefs: RefObject<Record<string, HTMLInputElement | null>>;
};

type CreatorInputValidation = {
  invalidRowIds: string[];
  issues: CampaignCreateIssue[];
};

function buildNextInputValueAfterPaste(input: HTMLInputElement, pastedText: string) {
  const { value, selectionStart, selectionEnd } = input;
  const start = selectionStart ?? value.length;
  const end = selectionEnd ?? value.length;

  return `${value.slice(0, start)}${pastedText}${value.slice(end)}`;
}

const CreatorTableRow = memo(function CreatorTableRow({
  editor,
  item,
  isInvalid,
  rowIssue,
}: CreatorRowProps) {
  const { actions, sourceRefs } = editor;

  return (
    <TableRow key={item.id} gridTemplateColumns={TABLE_GRID.creator} className="items-start">
      <TableCell className="flex-col items-stretch gap-1.5 px-3 py-3">
        <Input
          ref={(element) => {
            sourceRefs.current[item.id] = element;
          }}
          value={item.sourceValue}
          onChange={(event) => actions.changeSource(item.id, event.target.value)}
          onPaste={(event) => {
            const pastedText = event.clipboardData.getData('text');
            if (!pastedText) return;
            event.preventDefault();

            actions.pasteSource(
              item.id,
              buildNextInputValueAfterPaste(event.currentTarget, pastedText),
            );
          }}
          onBlur={() => actions.blurSource(item.id)}
          placeholder="creatorhandle or profile link"
          aria-invalid={isInvalid}
          className={cn(
            'h-9 px-2.5 text-sm',
            isInvalid && 'border-destructive ring-destructive/20',
          )}
        />
        {rowIssue ? <p className="text-destructive text-xs">{rowIssue}</p> : null}
      </TableCell>
      <TableCell className="flex-col items-stretch gap-1.5 px-3 py-3">
        <Select
          value={item.platform || undefined}
          onValueChange={(value) => actions.changePlatform(item.id, value as SOURCE_PLATFORM_TYPE)}
        >
          <SelectTrigger className="bg-card h-9 text-sm">
            {item.platform ? (
              <PlatformSelectLabel platform={item.platform} />
            ) : (
              <SelectValue placeholder="Select platform" />
            )}
          </SelectTrigger>
          <SelectContent>
            {/* PLATFORM_EXPANSION_MARK: Render additional platform options here when campaign tracking expands beyond TikTok. */}
            {CAMPAIGN_SUPPORTED_PLATFORMS.map((platform) => (
              <SelectItem key={platform} value={platform}>
                <PlatformSelectLabel platform={platform} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="flex-col items-stretch gap-1.5 px-3 py-3">
        <Select
          value={item.label || EMPTY_LABEL_VALUE}
          onValueChange={(value) =>
            actions.changeLabel(
              item.id,
              value === EMPTY_LABEL_VALUE ? undefined : (value as CAMPAIGN_LABEL_TYPE),
            )
          }
        >
          <SelectTrigger className="bg-card h-9 text-sm">
            <SelectValue placeholder="Select label" />
          </SelectTrigger>
          <SelectContent>
            {CampaignLabelOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="justify-end px-2 py-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => actions.remove(item.id)}
          aria-label="Remove creator"
        >
          <Trash2 className="size-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
});

function EditableCreatorTable({
  editor,
  items,
  creatorInvalidRowIds,
  creatorIssuesByRowId,
}: {
  editor: CreatorRowEditor;
  items: CampaignDraftCreatorItem[];
  creatorInvalidRowIds: Set<string>;
  creatorIssuesByRowId: Map<string, string>;
}) {
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[720px]">
        <TableBody>
          {items.map((item) => {
            return (
              <CreatorTableRow
                key={item.id}
                editor={editor}
                item={item}
                isInvalid={creatorInvalidRowIds.has(item.id)}
                rowIssue={creatorIssuesByRowId.get(item.id)}
              />
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

type Props = {
  rows: CreatorInputRows;
  validation: CreatorInputValidation;
};

export function CampaignCreatorInputCanvas({ rows, validation }: Props) {
  const currentStepTablePageSize = CAMPAIGN_CREATE_TABLE_PAGE_SIZE;
  const [creatorTablePage, setCreatorTablePage] = useState(1);
  const [pendingFocusRowId, setPendingFocusRowId] = useState<string>();
  const { items, remainingSlots } = rows;
  const filledCreatorCount = items.filter((item) => item.sourceValue.trim().length > 0).length;
  const creatorCountBadge = getCreatorCountBadge(filledCreatorCount);
  const hasFilledRows = items.some((item) => item.sourceValue.trim().length > 0);
  const totalPages = Math.max(1, Math.ceil(items.length / currentStepTablePageSize));
  const shouldPaginate = items.length > currentStepTablePageSize;
  const pageStartIndex = (creatorTablePage - 1) * currentStepTablePageSize;
  const paginatedItems = items.slice(pageStartIndex, pageStartIndex + currentStepTablePageSize);
  const creatorIssuesByRowId = useMemo(
    () => new Map(validation.issues.map((issue) => [issue.rowId, issue.message])),
    [validation.issues],
  );
  const invalidRowIds = useMemo(
    () => new Set(validation.invalidRowIds),
    [validation.invalidRowIds],
  );
  const editor = useMemo(
    () => ({
      actions: rows.actions,
      sourceRefs: rows.sourceRefs,
    }),
    [rows.actions, rows.sourceRefs],
  );

  useEffect(() => {
    setCreatorTablePage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (!pendingFocusRowId) return;
    const target = rows.sourceRefs.current[pendingFocusRowId];
    if (!target) return;
    requestAnimationFrame(() => {
      focusElement(target);
    });
    setPendingFocusRowId(undefined);
  }, [items, pendingFocusRowId, rows.sourceRefs]);

  const handleAddMoreClick = () => {
    const targetRowId = rows.add();
    if (!targetRowId) return;

    const existingIndex = items.findIndex((item) => item.id === targetRowId);
    const nextIndex = existingIndex >= 0 ? existingIndex : items.length;
    setCreatorTablePage(Math.floor(nextIndex / currentStepTablePageSize) + 1);
    setPendingFocusRowId(targetRowId);
  };

  return (
    <div className="space-y-4">
      <div className="border-border bg-muted overflow-hidden rounded-lg border">
        <div className="border-border flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="text-foreground text-sm font-medium">Creators list</div>
            <Badge
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                'bg-primary text-primary-foreground hover:bg-primary border-transparent',
              )}
            >
              {creatorCountBadge}
            </Badge>
          </div>
          <button
            type="button"
            onClick={rows.clear}
            disabled={!hasFilledRows}
            className="text-muted-foreground hover:text-foreground disabled:text-muted-foreground/50 text-sm font-medium transition-colors disabled:pointer-events-none"
          >
            Clear all
          </button>
        </div>

        <div className="px-4 py-3">
          <EditableCreatorTable
            editor={editor}
            items={paginatedItems}
            creatorInvalidRowIds={invalidRowIds}
            creatorIssuesByRowId={creatorIssuesByRowId}
          />
        </div>

        <div className="border-border flex items-center justify-between gap-3 border-t px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddMoreClick}
            disabled={remainingSlots === 0}
            className="text-muted-foreground hover:text-foreground h-auto px-0 text-sm font-medium hover:bg-transparent"
          >
            <Plus className="size-4" />
            Add more
          </Button>

          {shouldPaginate ? (
            <PageNavigationView
              className="p-0"
              startIndex={pageStartIndex + 1}
              endIndex={Math.min(pageStartIndex + currentStepTablePageSize, items.length)}
              totalCount={items.length}
              onPrevious={() => setCreatorTablePage((page) => Math.max(page - 1, 1))}
              onNext={() => setCreatorTablePage((page) => Math.min(page + 1, totalPages))}
              canGoPrevious={creatorTablePage > 1}
              canGoNext={creatorTablePage < totalPages}
            />
          ) : (
            <div />
          )}
        </div>
      </div>
      {remainingSlots === 0 ? (
        <Alert className="border-warning/30 bg-warning/10">
          <AlertTriangle className="text-warning size-4" />
          <AlertDescription className="text-warning mt-1 text-sm">
            Creator tracking limit reached. Remove a creator to add another one.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
